import { JwtPayload, jwtDecode } from 'jwt-decode';

import { User } from '../entities/User';

/** .NET ClaimTypes.Name / Email when outbound claim mapping is disabled. */
const CLAIM_NAME = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';
const CLAIM_EMAIL = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';

type DecodedPortalToken = JwtPayload & {
  userName?: string;
  preferred_username?: string;
  unique_name?: string;
  dbName?: string;
  DbName?: string;
  cusId?: string | number;
  userID?: number;
  userId?: number;
  id?: string;
  nameid?: string;
  email?: string;
  Email?: string;
  firstName?: string;
  FirstName?: string;
  given_name?: string;
  lastName?: string;
  LastName?: string;
  family_name?: string;
  name?: string;
  fullName?: string;
  FullName?: string;
  role?: string;
  Role?: string;
  [CLAIM_NAME]?: string;
  [CLAIM_EMAIL]?: string;
};

/**
 * Domain service for authentication-related operations
 * Contains pure domain logic related to authentication
 */
export class AuthDomainService {
  /**
   * Verifies if a token is valid and not expired
   */
  verifyToken(token: string): boolean {
    if (!token) return false;

    return true;

    // try {
    //   const decoded = jwtDecode<JwtPayload>(token);
    //   return Boolean(decoded.exp && decoded.exp > Date.now() / 1000);
    // } catch {
    //   throw new TokenVerificationError();
    // }
  }

  /**
   * Extracts user information from JWT token
   */
  extractUserFromToken(token: string): User | null {
    if (!token) return null;

    try {
      const decoded = jwtDecode<DecodedPortalToken>(token);

      // ContractorsAPI emits unique_name / email / cusId / sub (and sometimes long ClaimTypes URIs).
      // Merchant-style tokens may still use userName / dbName.
      const userName =
        decoded.userName ??
        decoded.preferred_username ??
        decoded.unique_name ??
        decoded[CLAIM_NAME] ??
        decoded.name;

      if (!userName) return null;

      const email = decoded.email ?? decoded.Email ?? decoded[CLAIM_EMAIL];
      const firstName = decoded.firstName ?? decoded.FirstName ?? decoded.given_name;
      const lastName = decoded.lastName ?? decoded.LastName ?? decoded.family_name;
      const composedName = [firstName, lastName].filter(Boolean).join(' ') || undefined;
      const fullName = decoded.fullName ?? decoded.FullName ?? composedName ?? decoded.name ?? userName;
      const role = decoded.role ?? decoded.Role;
      const dbName = decoded.dbName ?? decoded.DbName;
      const cusId = decoded.cusId != null ? String(decoded.cusId) : undefined;
      const subjectId = decoded.sub ?? decoded.nameid ?? decoded.id;
      const userID = decoded.userID ?? decoded.userId ?? (subjectId ? Number(subjectId) : undefined);

      return {
        id: subjectId,
        userID: Number.isFinite(userID) ? userID : undefined,
        userName,
        dbName,
        cusId,
        email,
        firstName,
        lastName,
        fullName,
        role,
      };
    } catch {
      return null;
    }
  }

  /**
   * Checks if the provided credentials meet security requirements
   */
  validateCredentialStrength(password: string): {
    isValid: boolean;
    message?: string;
  } {
    if (password.length < 8) {
      return {
        isValid: false,
        message: 'Password must be at least 8 characters long',
      };
    }

    // Additional validation logic can be added here
    return { isValid: true };
  }
}
